/**
 * Map widget — draws the dashboard's own layers.
 *
 * A dashboard map is a small tile, not the full Map App, so it carries its own
 * compact controls (zoom, home, basemap) rather than borrowing the builder's
 * toolbar. The same component renders in the builder, the live preview and the
 * published viewer, so those controls exist in all three by construction.
 *
 * It takes part in actions from both ends:
 *  - SOURCE: panning or zooming broadcasts the visible extent.
 *  - TARGET: a selection elsewhere is highlighted here, and can zoom or pan the
 *    map to the matching features.
 *
 * NOTE on services: MapView takes ONE GeoServer URL and ONE ArcGIS URL, so when
 * sources span several services only the first of each is drawn, and the
 * footnote reports what was left out rather than silently dropping it.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { Home, Layers, Minus, Plus } from 'lucide-react';
import MapViewOL from '../../MapViewOL';
import WidgetFrame from './WidgetFrame';
import Tooltip from '../../components/glass/Tooltip';
import { useSourceFields } from './useSourceFields';
import {
  fetchMatchingGeometry,
  fetchSourceExtent,
  geoJsonBounds,
} from '../services/dashboardDataService';
import { buildConstraint } from '../utils/actionRuntime';
import { validateWidget } from '../utils/dashboardUtils';
import type {
  ActionSignal,
  DashboardDataSource,
  DashboardWidget,
} from '../types/dashboardTypes';
import type { LayerRenderer } from '../../types';
import type { LayerLabeling } from '../../labeling/LabelTypes';

type Basemap = 'street' | 'terrain' | 'satellite' | 'dark';

const BASEMAP_OPTIONS: { id: Basemap; label: string }[] = [
  { id: 'street', label: 'Street' },
  { id: 'terrain', label: 'Terrain' },
  { id: 'satellite', label: 'Satellite' },
  { id: 'dark', label: 'Dark canvas' },
];

interface Props {
  widget: DashboardWidget;
  dataSources: DashboardDataSource[];
  accentColor: string;
  dividerColor?: string;
  /** `dataSourceId → WHERE` from the header's selectors — see `selectorRuntime.ts`.
   *  Unlike every other widget type, a map can draw several sources at once,
   *  so it needs the full map rather than one pre-resolved string. */
  selectorFilters?: Record<string, string>;
  /** Dashboard theme, so an unset basemap can default to something readable. */
  themeMode?: 'light' | 'dark';
  /** Broadcasts the visible extent when this map drives a Filter action. */
  onSignal?: (sourceId: string, signal: ActionSignal | null) => void;
  /** Selections to highlight here (a `select` action targeted this map). */
  selectSignals?: ActionSignal[];
  /** Selections that should move the map (`zoom` / `pan` actions). */
  zoomSignals?: ActionSignal[];
  panSignals?: ActionSignal[];
  /** Changes when the tile resizes so the map re-measures its container. */
  resizeToken?: number | string;
}

/** Highlight styling — a hot outline that reads over any basemap. */
const HIGHLIGHT_RENDERER = {
  type: 'single' as const,
  color: '#F59E0B',
  fillOpacity: 0.35,
  outlineColor: '#B45309',
  outlineWidth: 3,
  pointSize: 22,
  symbolId: 'circle',
  lineStyle: 'solid' as const,
  fillStyle: 'solid' as const,
  outlineStyle: 'solid' as const,
  defaultColor: '#F59E0B',
};

export default function MapWidget({
  widget,
  dataSources,
  accentColor,
  dividerColor,
  selectorFilters = {},
  themeMode = 'light',
  onSignal,
  selectSignals,
  zoomSignals,
  panSignals,
  resizeToken,
}: Props) {
  const problems = validateWidget(widget);

  /* The map only broadcasts once the admin has given it an extent action with a
     target — otherwise every pan would refetch widgets that are not listening. */
  const drivesOthers = (widget.actions ?? []).some(
    (action) => action.trigger === 'extent' && action.targetWidgetIds.length > 0,
  );
  const emitExtent = drivesOthers
    ? (bounds: [number, number, number, number]) =>
        onSignal?.(widget.id, {
          kind: 'extent',
          sourceId: widget.id,
          sourceTitle: widget.title,
          extent: bounds,
        })
    : undefined;

  const selected = useMemo(
    () =>
      (widget.config.mapDataSourceIds ?? [])
        .map((id) => dataSources.find((source) => source.id === id))
        .filter((source): source is DashboardDataSource => Boolean(source)),
    [widget.config.mapDataSourceIds, dataSources],
  );

  // Group by service, keeping only the first URL of each type (see file note).
  const {
    geoServerUrl,
    geoServerLayers,
    geoServerLayerStyles,
    arcgisUrl,
    arcgisLayers,
    arcgisLayerStyles,
    fileSources,
    labelEsriSources,
    skipped,
  } = useMemo(() => {
      const geoSources = selected.filter((source) => source.serviceType === 'geoserver');
      const esriSources = selected.filter((source) => source.serviceType === 'arcgis');
      /* Imported layers are not grouped by URL because they have none: each
         carries its own features, so ALL of them draw — the one-service-per-
         type limit that skips the others simply does not apply here. Their
         header-selector filtering is a known gap, not new here — this map
         never filtered a local source's own base draw, only remote ones (see
         file note on the highlight overlay being the one thing that touches
         local sources at all). */
      const localSources = selected.filter(
        (source) => source.serviceType === 'local' && source.localData,
      );

      const primaryGeoUrl = geoSources[0]?.serviceUrl ?? '';
      const primaryEsriUrl = esriSources[0]?.serviceUrl ?? '';

      const usedGeo = geoSources.filter((source) => source.serviceUrl === primaryGeoUrl);
      const usedEsri = esriSources.filter((source) => source.serviceUrl === primaryEsriUrl);

      // Each remote source's own share of the header's selectors, keyed the
      // way MapView wants them: by WMS layer name, or by ArcGIS layer id.
      // Symbology/labels come from this widget's OWN mapLayerStyles — see
      // dashboardTypes.ts's doc on why styling lives on the widget, not the
      // source.
      const geoStyles: Record<string, { filter?: string; renderer?: LayerRenderer; labeling?: LayerLabeling }> = {};
      for (const source of usedGeo) {
        const where = selectorFilters[source.id];
        const style = widget.config.mapLayerStyles?.[source.id];
        if (where || style?.renderer || style?.labeling) {
          geoStyles[source.layerName] = { filter: where, renderer: style?.renderer, labeling: style?.labeling };
        }
      }
      // ArcgisLayerStyle has no `labeling` field (only WmsLayerStyle extends
      // it with one) — ArcGIS labels go through the separate `labelLayers`
      // prop built below instead.
      const esriStyles: Record<number, { filter?: string; renderer?: LayerRenderer }> = {};
      for (const source of usedEsri) {
        const where = selectorFilters[source.id];
        const renderer = widget.config.mapLayerStyles?.[source.id]?.renderer;
        if ((where || renderer) && source.arcgisLayerId !== undefined) {
          esriStyles[source.arcgisLayerId] = { filter: where, renderer };
        }
      }

      return {
        geoServerUrl: primaryGeoUrl,
        geoServerLayers: usedGeo.map((source) => source.layerName),
        geoServerLayerStyles: geoStyles,
        arcgisUrl: primaryEsriUrl,
        arcgisLayers: usedEsri
          .map((source) => source.arcgisLayerId)
          .filter((id): id is number => id !== undefined),
        arcgisLayerStyles: esriStyles,
        fileSources: localSources,
        // Kept around for the labelLayers useMemo below — ArcGIS labeling
        // has to match the SAME "first service URL only" set esriStyles
        // drew from, or it would try to label a layer the map never drew.
        labelEsriSources: usedEsri,
        skipped: selected.length - usedGeo.length - usedEsri.length - localSources.length,
      };
    }, [selected, selectorFilters, widget.config.mapLayerStyles]);

  // Frame the map on the first selected layer unless the admin fixed a view.
  const [view, setView] = useState<{ center: [number, number]; zoom: number }>({
    center: widget.config.center ?? [28.7041, 77.1025],
    zoom: widget.config.zoom ?? 11,
  });
  /* Where the Home button returns to. Captured from whatever the map first
     settled on, so Home means "the view this widget was set up with" rather
     than an arbitrary hard-coded place. */
  const homeView = useRef(view);

  const [basemap, setBasemap] = useState<Basemap | null>(null);
  const [basemapOpen, setBasemapOpen] = useState(false);
  const activeBasemap: Basemap =
    basemap ?? widget.config.basemap ?? (themeMode === 'dark' ? 'dark' : 'street');

  const firstSourceKey = selected[0]
    ? `${selected[0].serviceUrl}|${selected[0].layerName}|${selected[0].arcgisLayerId ?? ''}`
    : '';

  useEffect(() => {
    if (widget.config.center || !selected[0]) return undefined;

    let active = true;
    void fetchSourceExtent(selected[0]).then((bounds) => {
      if (!active || !bounds) return;
      const [west, south, east, north] = bounds;
      const span = Math.max(Math.abs(east - west), Math.abs(north - south), 0.01);
      const next = {
        center: [(south + north) / 2, (west + east) / 2] as [number, number],
        zoom: Math.max(3, Math.min(16, Math.floor(Math.log2(360 / span) + 1))),
      };
      homeView.current = next;
      setView(next);
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firstSourceKey, widget.config.center]);

  /* ── Selection highlight ──────────────────────────────────────────────────
     The incoming signals are turned into a where clause the same way a chart
     would, then the matching SHAPES are fetched and drawn as an extra vector
     layer above the service layers. */
  /* Field types matter: a selection of 8 on an integer column has to become
     `quality = 8`, not `quality = '8'` — ArcGIS rejects the quoted form and the
     highlight silently never appears. */
  const highlightSource = selected[0];
  const { fields: highlightFields } = useSourceFields(highlightSource);

  const highlightWhere = useMemo(() => {
    const all = [...(selectSignals ?? []), ...(zoomSignals ?? []), ...(panSignals ?? [])];
    // De-duplicate: one source may drive select AND zoom on the same map.
    const seen = new Set<string>();
    const unique = all.filter((signal) => {
      const key = `${signal.sourceId}|${signal.kind}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return buildConstraint(unique, highlightFields).where;
  }, [selectSignals, zoomSignals, panSignals, highlightFields]);

  const [highlight, setHighlight] = useState<GeoJSON.FeatureCollection | null>(null);

  useEffect(() => {
    if (!highlightWhere || !highlightSource) {
      setHighlight(null);
      return undefined;
    }

    let active = true;
    void fetchMatchingGeometry(highlightSource, highlightWhere).then((collection) => {
      if (active) setHighlight(collection);
    });

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightWhere, firstSourceKey]);

  /* Zoom / pan follow the highlight once it has arrived — the geometry has to
     exist before there is anything to frame. */
  const wantsZoom = (zoomSignals?.length ?? 0) > 0;
  const wantsPan = (panSignals?.length ?? 0) > 0;

  useEffect(() => {
    if (!highlight || (!wantsZoom && !wantsPan)) return;
    const bounds = geoJsonBounds(highlight);
    if (!bounds) return;

    const [west, south, east, north] = bounds;
    const centre: [number, number] = [(south + north) / 2, (west + east) / 2];

    if (wantsPan && !wantsZoom) {
      // Pan keeps the current scale; only the centre moves.
      setView((current) => ({ ...current, center: centre }));
      return;
    }

    const span = Math.max(Math.abs(east - west), Math.abs(north - south), 0.002);
    setView({
      center: centre,
      zoom: Math.max(3, Math.min(18, Math.floor(Math.log2(360 / span)))),
    });
  }, [highlight, wantsZoom, wantsPan]);

  /* Imported sources drawn from the features they carry, with the selection
     highlight LAST so it paints over them rather than under. */
  const localLayers = useMemo(() => {
    const drawn = fileSources.map((source) => ({
      id: source.id,
      data: source.localData as GeoJSON.FeatureCollection,
      style: { renderer: widget.config.mapLayerStyles?.[source.id]?.renderer },
    }));
    return highlight
      ? [
          ...drawn,
          { id: `${widget.id}-highlight`, data: highlight, style: { renderer: HIGHLIGHT_RENDERER } },
        ]
      : drawn;
  }, [fileSources, highlight, widget.id, widget.config.mapLayerStyles]);

  /* Local layers' labels (no service to embed a label SLD into, unlike
     GeoServer) and ArcGIS layers' labels (no server-side label SLD
     either) both ride MapViewOL's own client-side label-overlay prop
     instead — GeoServer needs no entry here, its labels are already folded
     into geoServerLayerStyles[name].labeling above. */
  const labelLayers = useMemo(() => {
    const entries: { id: string; labeling: LayerLabeling; features?: GeoJSON.Feature[]; arcgisLayerId?: number }[] = [];
    for (const source of fileSources) {
      const labeling = widget.config.mapLayerStyles?.[source.id]?.labeling;
      if (labeling && source.localData) {
        entries.push({ id: source.id, labeling, features: source.localData.features });
      }
    }
    for (const source of labelEsriSources) {
      const labeling = widget.config.mapLayerStyles?.[source.id]?.labeling;
      if (labeling && source.arcgisLayerId !== undefined) {
        entries.push({ id: source.id, labeling, arcgisLayerId: source.arcgisLayerId });
      }
    }
    return entries;
  }, [fileSources, labelEsriSources, widget.config.mapLayerStyles]);

  const highlightCount = highlight?.features?.length ?? 0;
  const footnote = [
    `${geoServerLayers.length + arcgisLayers.length + fileSources.length} layer${
      geoServerLayers.length + arcgisLayers.length + fileSources.length === 1 ? '' : 's'
    }`,
    highlightCount > 0
      ? `${highlightCount} selected${highlightCount >= 500 ? ' (first 500)' : ''}`
      : '',
    skipped > 0 ? `${skipped} from another service not shown` : '',
  ]
    .filter(Boolean)
    .join(' · ');

  const controlButton =
    'flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-white/95 text-slate-600 shadow-sm transition hover:bg-slate-100 hover:text-slate-900';

  return (
    <WidgetFrame
      title={widget.title}
      status="ready"
      incomplete={problems}
      accentColor={accentColor}
      outlineColor={widget.config.outlineColor ?? dividerColor}
      footnote={footnote}
    >
      <div className="relative h-full w-full">
        <MapViewOL
          /* A dashboard tile is a few hundred pixels of overview, not a sheet
             anyone takes a measurement off, and the chip would cover a real
             part of it. */
          showScale={false}
          // Drives the extent action; undefined when this map targets nothing,
          // so an idle map costs no extra work on every pan.
          onBoundsChange={emitExtent}
          /* An explicit choice always wins; only the unset case follows the
             dashboard theme, so a bright street map does not glare out of an
             otherwise dark board. */
          basemap={activeBasemap}
          center={view.center}
          zoom={view.zoom}
          geoServerUrl={geoServerUrl}
          geoServerLayers={geoServerLayers}
          geoServerLayerStyles={geoServerLayerStyles}
          arcgisUrl={arcgisUrl}
          arcgisLayers={arcgisLayers}
          arcgisLayerStyles={arcgisLayerStyles}
          localLayers={localLayers}
          labelLayers={labelLayers}
          /* No identify card in a dashboard tile. Imported sources are
             hit-testable now, so without this a click would open the map app's
             full-size feature card inside a widget far too small to hold it —
             and a click here already means "narrow the other widgets". */
          popupSuppressed
          containerResizeToken={resizeToken}
          onViewChange={(center, zoom) => setView({ center, zoom })}
        />

        {/* Compact controls. Above the map's own layers but inside the tile,
            so they never escape a widget onto a neighbour. */}
        <div className="absolute left-2 top-2 z-[800] flex flex-col gap-1">
          <Tooltip content="Zoom in one level" placement="right">
            <button
              type="button"
              aria-label="Zoom in"
              onClick={() => setView((current) => ({ ...current, zoom: Math.min(18, current.zoom + 1) }))}
              className={controlButton}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
          <Tooltip content="Zoom out one level" placement="right">
            <button
              type="button"
              aria-label="Zoom out"
              onClick={() => setView((current) => ({ ...current, zoom: Math.max(1, current.zoom - 1) }))}
              className={controlButton}
            >
              <Minus className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
          <Tooltip
            title="Home extent"
            content="Return to the view this map started at, undoing any zooming or panning."
            placement="right"
          >
            <button
              type="button"
              aria-label="Home extent"
              onClick={() => setView(homeView.current)}
              className={controlButton}
            >
              <Home className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        </div>

        <div className="absolute right-2 top-2 z-[800]">
          <Tooltip content="Change the background map" placement="left">
            <button
              type="button"
              aria-label="Change basemap"
              aria-expanded={basemapOpen}
              onClick={() => setBasemapOpen((open) => !open)}
              className={controlButton}
            >
              <Layers className="h-3.5 w-3.5" />
            </button>
          </Tooltip>

          {basemapOpen && (
            <ul
              role="menu"
              aria-label="Basemap"
              className="absolute right-0 mt-1 w-32 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
            >
              {BASEMAP_OPTIONS.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={activeBasemap === option.id}
                    onClick={() => {
                      setBasemap(option.id);
                      setBasemapOpen(false);
                    }}
                    className={`block w-full px-2.5 py-1.5 text-left text-[11px] transition hover:bg-slate-100 ${
                      activeBasemap === option.id
                        ? 'font-semibold text-blue-700'
                        : 'text-slate-600'
                    }`}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </WidgetFrame>
  );
}
